import { Helmet } from "react-helmet";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import AllocBarCanvas from "components/AllocBarCanvas";
import { useAppContext } from "components/Context/AppContext";
import { Chart, registerables } from "chart.js";
import "styles/portfolio.css";

Chart.register(...registerables);

const DASHBOARD_API =
  process.env.REACT_APP_API_URL || "http://localhost:5050/api";

// ─────────────────────────────────────────────────────────────
// PRESENTATION CONFIG (no data — just styling)
// ─────────────────────────────────────────────────────────────
const BENCHMARKS = {
  sp500:     { label:"S&P 500",    color:"rgba(201,168,76,0.75)",  dash:[5,4] },
  blend6040: { label:"60/40",      color:"rgba(184,123,200,0.75)", dash:[4,4] },
  msci:      { label:"MSCI World", color:"rgba(34,212,126,0.75)",  dash:[6,3] },
};
const PERIOD_KEYS = ["1M","3M","1Y","3Y","ALL"];

// ─────────────────────────────────────────────────────────────
// FORMATTERS
// ─────────────────────────────────────────────────────────────
const fmtM  = (v) => v == null ? "—" : v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : `$${v.toLocaleString()}`;
const fmtPnL = (v) => {
  if (v == null) return "—";
  const abs = Math.abs(v), sign = v >= 0 ? "+" : "-";
  if (abs >= 1e6) return `${sign}$${(abs/1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs/1e3).toFixed(0)}K`;
  return `${sign}$${abs.toLocaleString()}`;
};
const fmtPct = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;

// ─────────────────────────────────────────────────────────────
// BADGE HELPER
// ─────────────────────────────────────────────────────────────
const BADGE_CLASS = {
  Equity: "pf-badge pf-badge-equity",
  Bond:   "pf-badge pf-badge-bond",
  Alt:    "pf-badge pf-badge-alt",
  Cash:   "pf-badge pf-badge-cash",
  Deriv:  "pf-badge pf-badge-deriv",
};
function badgeClass(cls) { return BADGE_CLASS[cls] || "pf-badge pf-badge-other"; }

const DOT_COLORS = ["#4e8fdd","#22d47e","#C9A84C","#b87bc8","#e05252","#5a8a78","#d47e22","#dd7e4e"];

// ─────────────────────────────────────────────────────────────
// RETURN BAR SVG COMPONENT
// ─────────────────────────────────────────────────────────────
function ReturnBar({ data }) {
  if (!data || !data.length) return null;
  const available = data.filter(d => d.value != null);
  const maxAbs = available.length ? Math.max(...available.map(d => Math.abs(d.value))) : 1;
  const H = 80, ZERO_Y = 54, ZONE = 42;

  return (
    <div style={{ position: "relative", height: H, display: "flex" }}>
      {/* Zero line */}
      <div style={{ position: "absolute", left: 0, right: 0, top: ZERO_Y, height: 1, background: "rgba(255,255,255,0.12)", zIndex: 0 }} />

      {data.map((d) => {
        const noData = d.value == null;
        const isPos  = !noData && d.value >= 0;
        const bh     = noData ? 0 : Math.max(Math.round((Math.abs(d.value) / maxAbs) * ZONE), 2);
        const barColor   = isPos ? "#1e4a7a" : "var(--dash-red)";
        const labelColor = noData ? "var(--dash-muted)" : isPos ? "var(--dash-green)" : "var(--dash-red)";
        const sign = !noData && d.value > 0 ? "+" : "";

        return (
          <div key={d.label} style={{ flex: 1, position: "relative" }}>
            {!noData && (
              <div style={{
                position: "absolute",
                left: "50%", transform: "translateX(-50%)",
                width: 32, height: bh,
                background: barColor,
                borderRadius: isPos ? "3px 3px 0 0" : "0 0 3px 3px",
                bottom: isPos ? H - ZERO_Y : "auto",
                top:    isPos ? "auto"      : ZERO_Y,
              }} />
            )}
            <div style={{
              position: "absolute",
              left: "50%", transform: "translateX(-50%)",
              whiteSpace: "nowrap",
              fontFamily: "var(--dash-font-m)", fontSize: "0.58rem", fontWeight: 500,
              color: labelColor,
              bottom: noData ? (H - ZERO_Y + 3) : isPos ? (H - ZERO_Y + bh + 3) : "auto",
              top:    (!noData && !isPos) ? (ZERO_Y + bh + 3) : "auto",
            }}>
              {noData ? "—" : `${sign}${d.value}%`}
            </div>
            <div style={{
              position: "absolute", bottom: 0,
              left: "50%", transform: "translateX(-50%)",
              whiteSpace: "nowrap",
              fontFamily: "var(--dash-font-b)", fontSize: "0.58rem",
              color: "var(--dash-muted)",
            }}>
              {d.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DRILL HOLDINGS TABLE (right panel)
// ─────────────────────────────────────────────────────────────
function DrillTable({ data }) {
  const { cols, rows, foot } = data;
  return (
    <table className="pf-holdings-table pf-drill-table" style={{ width: "100%" }}>
      <thead>
        <tr>
          {cols.map((c, i) => (
            <th key={i} className={i === 0 ? "left" : ""}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri}>
            {row.map((cell, ci) => {
              if (ci === 0) {
                const dot = DOT_COLORS[ri % DOT_COLORS.length];
                return (
                  <td key={ci} className="left">
                    <div className="pf-dot-cell">
                      <div className="pf-dot" style={{ background: dot, boxShadow: `0 0 5px ${dot}88` }} />
                      <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.74rem", fontWeight: 500, color: "var(--dash-text)" }}>{cell}</span>
                    </div>
                  </td>
                );
              }
              const isPos = cell.startsWith("+");
              const isNeg = cell.startsWith("-") || cell.startsWith("−");
              const col = isPos ? "var(--dash-green)" : isNeg ? "var(--dash-red)" : "var(--dash-text)";
              return (
                <td key={ci} style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.73rem", color: col }}>
                  {cell}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          {foot.map((cell, ci) => {
            const isPos = cell.startsWith("+");
            const isNeg = cell.startsWith("-") || cell.startsWith("−");
            const col = isPos ? "var(--dash-green)" : isNeg ? "var(--dash-red)" : "var(--dash-text)";
            return (
              <td key={ci} className={ci === 0 ? "left" : ""}
                style={{ fontFamily: ci === 0 ? "var(--dash-font-b)" : "var(--dash-font-m)", fontSize: "0.73rem", fontWeight: 600, color: col }}>
                {cell}
              </td>
            );
          })}
        </tr>
      </tfoot>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────
const SLICE_TABS = ["asset","broker","region","industry","currency"];
const SLICE_LABELS = { asset:"Asset type", broker:"Broker", region:"Region", industry:"Industry", currency:"Currency" };

const POS_COLS = [
  { label:"Ticker",   key:"ticker",        align:"left"  },
  { label:"Name",     key:"name",          align:"left"  },
  { label:"Class",    key:"assetClass",    align:"right" },
  { label:"CCY",      key:"currency",      align:"right" },
  { label:"Mkt Val",  key:"marketValue",   align:"right" },
  { label:"Wt %",     key:"weight",        align:"right" },
  { label:"Day P&L",  key:"dayPnL",        align:"right" },
  { label:"Day %",    key:"dayReturn",     align:"right" },
  { label:"MTD %",    key:"mtdReturn",     align:"right" },
  { label:"YTD %",    key:"ytdReturn",     align:"right" },
  { label:"1Y %",     key:"oneYearReturn", align:"right" },
  { label:"Tail VaR", key:"varContrib",    align:"right" },
];

export default function PortfolioPage() {
  const { selectedAccount } = useAppContext();

  // ── API state ──
  const [summary,   setSummary]   = useState(null);
  const [positions, setPositions] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [allocData, setAllocData] = useState(null);
  const [loading,   setLoading]   = useState(false);

  // ── Chart state ──
  const [period,        setPeriod]       = useState("1Y");
  const [activeBenches, setActiveBenches]= useState(new Set(["sp500"]));
  const chartRef    = useRef(null);
  const chartInst   = useRef(null);
  const fsChartRef  = useRef(null);
  const fsChartInst = useRef(null);

  // ── Positions state ──
  const [posFilter, setPosFilter] = useState("All");
  const [sortKey,   setSortKey]   = useState("marketValue");
  const [sortDir,   setSortDir]   = useState("desc");

  // ── Allocation state ──
  const [slice,      setSlice]      = useState("asset");
  const [allocLevel, setAllocLevel] = useState("all");
  const [drillMode,  setDrillMode]  = useState(null);

  // ── Fullscreen state ──
  const [fsPanel, setFsPanel] = useState(null);

  // ─────────────────────────────────────────────────────────────
  // FETCHES
  // ─────────────────────────────────────────────────────────────

  // Summary + positions (when account changes)
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const aid   = selectedAccount?.account_id;
    if (!token || !aid) return;

    const q = `?token=${token}&account_id=${aid}`;
    setLoading(true);
    Promise.all([
      fetch(`${DASHBOARD_API}/portfolio/summary${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/portfolio/positions${q}`).then(r => r.json()),
    ])
      .then(([summ, pos]) => {
        setSummary(summ?.aum != null ? summ : null);
        setPositions(Array.isArray(pos) && pos.length ? pos : null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedAccount]);

  // Chart data (when period or account changes)
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const aid   = selectedAccount?.account_id;
    if (!token || !aid) return;

    fetch(`${DASHBOARD_API}/portfolio/chart/${period}?token=${token}&account_id=${aid}`)
      .then(r => r.json())
      .then(data => setChartData(data?.labels ? data : null))
      .catch(console.error);
  }, [period, selectedAccount]);

  // Allocation data (when slice or account changes)
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const aid   = selectedAccount?.account_id;
    if (!token || !aid) return;

    fetch(`${DASHBOARD_API}/portfolio/allocation?token=${token}&account_id=${aid}&slice=${slice}`)
      .then(r => r.json())
      .then(data => setAllocData(data?.levels ? data : null))
      .catch(console.error);

    setAllocLevel("all");
    setDrillMode(null);
  }, [slice, selectedAccount]);

  // ─────────────────────────────────────────────────────────────
  // CHART
  // ─────────────────────────────────────────────────────────────
  const buildChart = useCallback((canvasEl, instRef, data, benches) => {
    if (!canvasEl || !data) return;
    if (instRef.current) { instRef.current.destroy(); instRef.current = null; }
    const ctx  = canvasEl.getContext("2d");
    const grad = ctx.createLinearGradient(0, 0, 0, 220);
    grad.addColorStop(0, "rgba(78,143,221,0.16)");
    grad.addColorStop(1, "rgba(78,143,221,0.00)");

    const datasets = [{
      label: "Portfolio",
      data: data.portfolio,
      borderColor: "#4e8fdd", borderWidth: 1.8, pointRadius: 0, tension: 0.35,
      fill: true, backgroundColor: grad,
    }];
    Object.entries(BENCHMARKS).forEach(([key, b]) => {
      if (benches.has(key) && data[key]) {
        datasets.push({
          label: b.label,
          data: data[key],
          borderColor: b.color, borderWidth: 1.4, borderDash: b.dash,
          pointRadius: 0, tension: 0.35, fill: false,
        });
      }
    });

    // Determine display scale from max value across all datasets
    const allVals = datasets.flatMap(ds => ds.data || []).filter(v => v != null);
    const maxVal  = allVals.length ? Math.max(...allVals) : 0;
    const [divisor, unit] = maxVal >= 1.5e6 ? [1e6, "M"] : [1e3, "K"];
    const fmtY = v => `${(v / divisor).toFixed(1)}${unit}`;

    instRef.current = new Chart(ctx, {
      type: "line",
      data: { labels: data.labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(8,14,24,0.96)",
            borderColor: "rgba(255,255,255,0.08)", borderWidth: 1, padding: 9,
            titleColor: "rgba(255,255,255,0.4)", bodyColor: "rgba(255,255,255,0.85)",
            titleFont: { family: "'DM Mono', monospace", size: 11 },
            bodyFont:  { family: "'DM Mono', monospace", size: 13 },
            callbacks: { label: c => ` ${c.dataset.label}: ${fmtY(c.parsed.y)}` },
          },
        },
        scales: {
          x: { ticks: { color: "rgba(255,255,255,0.25)", font: { family: "'DM Mono',monospace", size: 11 }, maxTicksLimit: 7, maxRotation: 0 }, grid: { color: "rgba(255,255,255,0.04)" }, border: { color: "rgba(255,255,255,0.04)" } },
          y: { ticks: { color: "rgba(255,255,255,0.25)", font: { family: "'DM Mono',monospace", size: 11 }, callback: v => fmtY(v) }, grid: { color: "rgba(255,255,255,0.04)" }, border: { color: "rgba(255,255,255,0.04)" } },
        },
      },
    });
  }, []);

  useEffect(() => {
    buildChart(chartRef.current, chartInst, chartData, activeBenches);
    return () => { if (chartInst.current) { chartInst.current.destroy(); chartInst.current = null; } };
  }, [chartData, activeBenches, buildChart]);

  useEffect(() => {
    if (fsPanel?.type === "hist") {
      requestAnimationFrame(() => {
        buildChart(fsChartRef.current, fsChartInst, chartData, activeBenches);
      });
    } else {
      if (fsChartInst.current) { fsChartInst.current.destroy(); fsChartInst.current = null; }
    }
  }, [fsPanel, chartData, activeBenches, buildChart]);

  const toggleBench = (key) => {
    setActiveBenches(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // ─────────────────────────────────────────────────────────────
  // POSITIONS
  // ─────────────────────────────────────────────────────────────
  const positionsList = positions || [];

  const filters = useMemo(() => {
    const codes = [...new Set(positionsList.map(p => p.assetClass))].sort();
    return ["All", ...codes];
  }, [positionsList]);

  const rows = useMemo(() => {
    let list = posFilter === "All" ? positionsList : positionsList.filter(p => p.assetClass === posFilter);
    return [...list].sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [positionsList, posFilter, sortKey, sortDir]);

  const totals = useMemo(() => ({
    marketValue: rows.reduce((s, p) => s + (p.marketValue || 0), 0),
    weight:      rows.reduce((s, p) => s + (p.weight     || 0), 0),
    dayPnL:      rows.reduce((s, p) => s + (p.dayPnL     || 0), 0),
  }), [rows]);

  const onSort = (key) => {
    if (key === sortKey) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  // ─────────────────────────────────────────────────────────────
  // ALLOCATION DRILLDOWN
  // ─────────────────────────────────────────────────────────────
  const currentLevels = allocData?.levels ?? null;

  const handleAllocLevel = useCallback((levelKey) => {
    setAllocLevel(levelKey);
    if (levelKey === "all") {
      setDrillMode(null);
    } else {
      const h = allocData?.holdings?.[levelKey];
      const levelData = allocData?.levels?.[levelKey];
      const title = levelData?.parentLabel || levelKey;
      setDrillMode({ slice, levelKey, title, holdings: h });
    }
  }, [slice, allocData]);

  const handleSliceChange = (s) => {
    setSlice(s);
    setAllocLevel("all");
    setDrillMode(null);
  };

  // ─────────────────────────────────────────────────────────────
  // FULLSCREEN
  // ─────────────────────────────────────────────────────────────
  const openFs = (type, title) => setFsPanel({ type, title });
  const closeFs = () => setFsPanel(null);

  // ─────────────────────────────────────────────────────────────
  // RENDER HELPERS
  // ─────────────────────────────────────────────────────────────
  const posNumColor = (v) => v == null ? "pf-neu" : v > 0 ? "pf-green" : v < 0 ? "pf-red" : "pf-neu";

  const fundName = selectedAccount?.account_name || selectedAccount?.short_name || summary?.fundName || "Portfolio";

  function AllocBreadcrumb() {
    if (!currentLevels || allocLevel === "all") return null;
    const sliceLabel = SLICE_LABELS[slice];
    const crumbs = [];
    let cur = allocLevel;
    while (cur && cur !== "all") {
      crumbs.unshift({ key: cur, label: currentLevels[cur]?.parentLabel || cur });
      cur = currentLevels[cur]?.parent;
    }
    return (
      <div className="pf-alloc-breadcrumb" style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
        <button className="bc-link" onClick={() => handleSliceChange(slice)}>{sliceLabel}</button>
        {crumbs.map((c, i) => (
          <span key={c.key} style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {" › "}
            {i < crumbs.length - 1
              ? <button className="bc-link" onClick={() => handleAllocLevel(c.key)}>{c.label}</button>
              : <span style={{ color: "var(--dash-gold-text)" }}>{c.label}</span>
            }
          </span>
        ))}
      </div>
    );
  }

  function PosHeader() {
    if (drillMode) {
      return (
        <div className="pf-card-header">
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button className="pf-back-btn" onClick={() => { setDrillMode(null); setAllocLevel("all"); }}>
              ← Back to positions
            </button>
            <span className="pf-card-title" style={{ fontSize: "0.65rem" }}>{drillMode.title}</span>
          </div>
          <button className="pf-expand-btn" onClick={() => openFs("positions", drillMode.title)}>⛶</button>
        </div>
      );
    }
    return (
      <div className="pf-card-header">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div className="pf-card-title">Positions</div>
          <span className="pf-pos-count">{rows.length}</span>
          <button className="pf-expand-btn" onClick={() => openFs("positions", "Positions")}>⛶</button>
        </div>
        <div className="pf-filter-tabs">
          {filters.map(f => (
            <button key={f} className={`pf-filter-tab${posFilter === f ? " active" : ""}`}
              onClick={() => setPosFilter(f)}>{f}</button>
          ))}
        </div>
      </div>
    );
  }

  function PosTableFull() {
    if (!positions) return <div className="trg-no-data">No data</div>;
    return (
      <table className="pf-holdings-table" style={{ width: "100%" }}>
        <thead>
          <tr>
            {POS_COLS.map(c => (
              <th key={c.key}
                className={`${c.align === "left" ? "left" : ""}${sortKey === c.key ? " active" : ""}`}
                onClick={() => onSort(c.key)}>
                {c.label}
                <span style={{ marginLeft: 3, opacity: sortKey === c.key ? 1 : 0.25, fontSize: "0.5em" }}>
                  {sortKey === c.key ? (sortDir === "asc" ? "▲" : "▼") : "▼"}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.id}>
              <td className="left"><span className="pf-ticker">{p.ticker}</span></td>
              <td className="left"><span className="pf-name">{p.name}</span></td>
              <td><span className={badgeClass(p.assetClass)}>{p.assetClass}</span></td>
              <td><span className="pf-num pf-neu">{p.currency}</span></td>
              <td><span className="pf-mval">{fmtM(p.marketValue)}</span></td>
              <td><span className="pf-num pf-neu">{p.weight != null ? `${p.weight.toFixed(1)}%` : "—"}</span></td>
              <td><span className={`pf-num ${posNumColor(p.dayPnL)}`}>{fmtPnL(p.dayPnL)}</span></td>
              <td><span className={`pf-num ${posNumColor(p.dayReturn)}`}>{fmtPct(p.dayReturn)}</span></td>
              <td><span className={`pf-num ${posNumColor(p.mtdReturn)}`}>{fmtPct(p.mtdReturn)}</span></td>
              <td><span className={`pf-num ${posNumColor(p.ytdReturn)}`}>{fmtPct(p.ytdReturn)}</span></td>
              <td><span className={`pf-num ${posNumColor(p.oneYearReturn)}`}>{fmtPct(p.oneYearReturn)}</span></td>
              <td><span className={`pf-num ${p.varContrib > 7 ? "pf-red" : p.varContrib > 4 ? "" : "pf-neu"}`}
                style={p.varContrib > 4 && p.varContrib <= 7 ? { color: "#C9A84C" } : {}}>
                {p.varContrib != null ? `${p.varContrib.toFixed(1)}%` : "—"}
              </span></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="left" colSpan={4} style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.68rem", fontWeight: 600, color: "var(--dash-text)" }}>Total ({rows.length})</td>
            <td><span className="pf-mval">{fmtM(totals.marketValue)}</span></td>
            <td><span className="pf-num pf-neu">{totals.weight.toFixed(1)}%</span></td>
            <td><span className={`pf-num ${posNumColor(totals.dayPnL)}`}>{fmtPnL(totals.dayPnL)}</span></td>
            <td colSpan={5} />
          </tr>
        </tfoot>
      </table>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // JSX
  // ─────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Portfolio – Tail Risk Global</title>
        <meta name="description" content="Portfolio positions and performance." />
      </Helmet>

      <div className="pf-shell">
        <DashboardTopbar />
        <DashboardMenuBar activePage="Portfolio" />

        <main className="pf-body">

          {/* Page header */}
          <div className="pf-page-header">
            <div className="pf-page-header-left">
              <div className="pf-fund-eyebrow">Portfolio</div>
              <div className="pf-fund-divider" />
              <div className="pf-fund-name">{fundName}</div>
            </div>
          </div>

          {loading && <div className="trg-loading">Loading…</div>}

          {/* KPI strip */}
          <div className="pf-kpi-strip">
            <div className="pf-kpi-item">
              <div className="pf-kpi-label">Market Value</div>
              <div className="pf-kpi-value">
                {summary ? (summary.aum / 1e6).toFixed(1) : "—"}
                <span className="pf-unit">M</span>
              </div>
              <div className="pf-kpi-sub">USD · consolidated</div>
            </div>
            <div className="pf-kpi-item">
              <div className="pf-kpi-label">Unrealised Gain</div>
              <div className="pf-kpi-value green">
                {summary ? `+${(summary.unrealizedGain / 1e6).toFixed(1)}` : "—"}
                {summary && <span className="pf-unit">M</span>}
              </div>
            </div>
            <div className="pf-ret-pane">
              <div className="pf-kpi-label" style={{ marginBottom: "0.4rem", fontSize: "0.6rem" }}>Portfolio Return</div>
              <div style={{ flex: 1, minHeight: 0 }}>
                {summary?.returns
                  ? <ReturnBar data={summary.returns} />
                  : !loading && <div className="trg-no-data" style={{ fontSize: "0.6rem" }}>No data</div>
                }
              </div>
            </div>
          </div>

          {/* Main 2×2 grid */}
          <div className="pf-main-grid">

            {/* ── TOP-LEFT: Historical MV chart ── */}
            <div className="pf-card">
              <div className="pf-card-header">
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div className="pf-card-title">Historical Market Value</div>
                  <button className="pf-expand-btn" onClick={() => openFs("hist", "Historical Market Value")}>⛶</button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap", marginLeft: "auto" }}>
                  <div className="pf-period-btns">
                    {PERIOD_KEYS.map(p => (
                      <button key={p} className={`pf-period-btn${period === p ? " active" : ""}`}
                        onClick={() => setPeriod(p)}>{p}</button>
                    ))}
                  </div>
                  <div style={{ width: 1, height: 14, background: "var(--dash-border2)" }} />
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {Object.entries(BENCHMARKS).map(([key, b]) => (
                      <button key={key}
                        className={`pf-period-btn${activeBenches.has(key) ? " bench-active" : ""}`}
                        onClick={() => toggleBench(key)}>{b.label}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="pf-card-inner">
                <div className="pf-chart-legend">
                  <div className="pf-legend-item">
                    <div style={{ width: 16, height: 2, background: "var(--dash-blue)", borderRadius: 1 }} />
                    Portfolio
                  </div>
                  {Object.entries(BENCHMARKS).map(([key, b]) => activeBenches.has(key) && (
                    <div key={key} className="pf-legend-item">
                      <div style={{ width: 16, height: 0, borderTop: `2px dashed ${b.color}` }} />
                      {b.label}
                    </div>
                  ))}
                </div>
                <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
                  {!chartData && !loading && (
                    <div className="trg-no-data" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>No data</div>
                  )}
                  <canvas ref={chartRef} style={{ display: "block" }} />
                </div>
              </div>
            </div>

            {/* ── RIGHT COL (spans both rows): Positions / Drill ── */}
            <div className="pf-card pf-positions-col">
              <PosHeader />
              <div className="pf-holdings-body">
                {drillMode
                  ? (drillMode.holdings ? <DrillTable data={drillMode.holdings} /> : <div className="trg-no-data">No data</div>)
                  : <PosTableFull />
                }
              </div>
            </div>

            {/* ── BOTTOM-LEFT: Allocation chart ── */}
            <div className="pf-card pf-alloc-col">
              <div className="pf-card-header">
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div className="pf-card-title">Allocation</div>
                  <button className="pf-expand-btn" onClick={() => openFs("alloc", "Allocation")}>⛶</button>
                </div>
                <div className="pf-slice-tabs">
                  {SLICE_TABS.map(s => (
                    <button key={s} className={`pf-slice-tab${slice === s ? " active" : ""}`}
                      onClick={() => handleSliceChange(s)}>
                      {SLICE_LABELS[s]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pf-alloc-body">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
                  <span className="pf-alloc-hint">Click a bar to drill down</span>
                  <AllocBreadcrumb />
                </div>
                <div className="pf-alloc-canvas-wrap">
                  {currentLevels
                    ? (
                      <AllocBarCanvas
                        key={slice}
                        levels={currentLevels}
                        theme="dark"
                        initialLevel={allocLevel}
                        onLevelChange={handleAllocLevel}
                        showVar={false}
                      />
                    )
                    : !loading && <div className="trg-no-data">No data</div>
                  }
                </div>
              </div>
            </div>

          </div>{/* /pf-main-grid */}
        </main>
      </div>

      {/* ── Fullscreen overlay ── */}
      {fsPanel && (
        <div className={`pf-fs-overlay${fsPanel ? " active" : ""}`}
          onClick={e => { if (e.target === e.currentTarget) closeFs(); }}>
          <div className="pf-fs-panel">
            <div className="pf-fs-header">
              <div className="pf-fs-title">{fsPanel.title}</div>
              <button className="pf-fs-close" onClick={closeFs}>✕ close</button>
            </div>
            <div className="pf-fs-body">
              {fsPanel.type === "hist" && (
                <>
                  <div className="pf-chart-legend" style={{ flexShrink: 0 }}>
                    <div className="pf-legend-item">
                      <div style={{ width: 16, height: 2, background: "var(--dash-blue)", borderRadius: 1 }} />
                      Portfolio
                    </div>
                    {Object.entries(BENCHMARKS).map(([key, b]) => activeBenches.has(key) && (
                      <div key={key} className="pf-legend-item">
                        <div style={{ width: 16, height: 0, borderTop: `2px dashed ${b.color}` }} />
                        {b.label}
                      </div>
                    ))}
                  </div>
                  <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
                    <canvas ref={fsChartRef} style={{ display: "block" }} />
                  </div>
                </>
              )}
              {fsPanel.type === "positions" && (
                <div style={{ flex: 1, overflow: "auto" }}>
                  {drillMode
                    ? (drillMode.holdings ? <DrillTable data={drillMode.holdings} /> : <div className="trg-no-data">No data</div>)
                    : <PosTableFull />
                  }
                </div>
              )}
              {fsPanel.type === "alloc" && (
                <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.6rem" }}>
                    <span className="pf-alloc-hint">Click a bar to drill down</span>
                    <AllocBreadcrumb />
                  </div>
                  {currentLevels
                    ? (
                      <AllocBarCanvas
                        key={`fs-${slice}`}
                        levels={currentLevels}
                        theme="dark"
                        initialLevel={allocLevel}
                        onLevelChange={handleAllocLevel}
                        showVar={false}
                      />
                    )
                    : <div className="trg-no-data">No data</div>
                  }
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
